import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Phone, Heart } from "lucide-react";
import { WHATSAPP_URL, PHONE_TEL, PHONE_DISPLAY } from "./shared";

const HERO_IMG = "/hero-loja.jpg";

const LINES = ["SEU CELULAR CONSERTADO", "RÁPIDO E COM GARANTIA."];

const METRICS = [
  { value: "+7.000", label: "celulares consertados" },
  { value: "7 anos", label: "no mercado" },
  { value: "6 meses", label: "de garantia em nossos serviços" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt="Fachada da Pressi Celulares à noite, na Av. São Borja"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#252D33] via-[#252D33]/85 to-[#252D33]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#252D33] via-transparent to-[#252D33]/70" />
      </motion.div>
      <div className="absolute inset-0 bg-grid-millimetric opacity-60 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 pt-32 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-2.5 border border-[#ADCF5D]/30 bg-[#ADCF5D]/10 rounded-full px-4 py-1.5 mb-8"
          data-testid="hero-status-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping-dot absolute inline-flex h-full w-full rounded-full bg-[#ADCF5D]" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#ADCF5D]" />
          </span>
          <span className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[#ADCF5D]">
            Loja Aberta • Atendimento Imediato
          </span>
        </motion.div>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05] text-white max-w-4xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.85, delay: 0.15 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
              >
                {i === 1 ? (
                  <>
                    RÁPIDO E COM{" "}
                    <span className="text-[#99BA4B] text-glow-amber">GARANTIA.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-200"
        >
          A Pressi Celulares cuida do seu smartphone com carinho e técnica: telas, baterias,
          conectores, placas e software. Orçamento rápido pelo WhatsApp e garantia documentada —
          aqui no Rio Branco, em São Leopoldo.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <a
            data-testid="hero-whatsapp-btn"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2.5 bg-[#99BA4B] hover:bg-[#ADCF5D] text-[#1D2328] font-bold px-7 py-4 rounded-full text-base transition-all duration-150 active:scale-95 shadow-[0_10px_40px_rgba(153,186,75,0.35)]"
          >
            <MessageCircle size={19} strokeWidth={2.5} />
            Pedir Orçamento no WhatsApp
          </a>
          <a
            data-testid="hero-phone-btn"
            href={PHONE_TEL}
            className="inline-flex items-center justify-center gap-2.5 border border-white/20 hover:border-[#99BA4B]/60 bg-white/5 hover:bg-white/10 backdrop-blur-sm text-white font-semibold px-7 py-4 rounded-full text-base transition-all duration-150 active:scale-95"
          >
            <Phone size={18} />
            Ligar: {PHONE_DISPLAY}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4 border-t border-white/10 pt-6"
          data-testid="hero-metrics-bar"
        >
          <span className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.25em] uppercase text-zinc-400">
            <Heart size={13} className="text-[#99BA4B]" />
            Nossos Números
          </span>
          {METRICS.map((m) => (
            <div key={m.label} className="flex items-baseline gap-2">
              <span className="font-display text-xl sm:text-2xl text-white">{m.value}</span>
              <span className="font-mono-tech text-[11px] text-zinc-400 uppercase tracking-wider">
                {m.label}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
